import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  phoneScreenRoundName,
  type PhoneScreenMoveResult,
  type PhoneScreenPerson,
  type PhoneScreenResult,
  type PhoneScreenResultResponse,
  type PhoneScreenView,
  type StageBar,
} from '@nest-recruit/shared';
import { apiDelete, apiGet, apiPost, apiPut, errorMessage, type ApiError } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { stageBarKey } from '../../jobs/api/jobs-api';
import { shiftStageBar } from '../../jobs/stage-bar-shift';
import { undoAction } from '../../pipeline/api/pipeline-api';

// ADR 0079 — PHS-01, the phone Screening step. The view sits under `['applications']`, the key the API's
// hints name for the job room, so a colleague's Pass or Out reaches an open screen by itself (#12). Every
// change is drawn at once and rolled back on a refusal (§5a rule 1); the drawn state is held until the
// refetch lands (rule 3). Reversible moves carry the 10-second Undo (QoL §4).

export function phoneScreenKey(jobId: string): readonly unknown[] {
  return ['applications', 'phone-screen', jobId];
}

export function usePhoneScreen(jobId: string): UseQueryResult<PhoneScreenView, ApiError> {
  return useQuery<PhoneScreenView, ApiError>({
    queryKey: phoneScreenKey(jobId),
    queryFn: () => apiGet<PhoneScreenView>(`/jobs/${jobId}/phone-screen`),
    placeholderData: (previous) => previous,
  });
}

/** Draws a change on the cached view (and, if asked, on the bar); returns the way back. */
function draw(queryClient: QueryClient, jobId: string, change: (view: PhoneScreenView) => PhoneScreenView, bar?: (bar: StageBar) => StageBar): () => void {
  const key = phoneScreenKey(jobId);
  const before = queryClient.getQueryData<PhoneScreenView>(key);
  const barBefore = queryClient.getQueryData<StageBar>(stageBarKey(jobId));
  if (before) queryClient.setQueryData<PhoneScreenView>(key, change(before));
  if (bar && barBefore) queryClient.setQueryData<StageBar>(stageBarKey(jobId), bar(barBefore));
  return () => {
    if (before) queryClient.setQueryData(key, before);
    if (barBefore) queryClient.setQueryData(stageBarKey(jobId), barBefore);
  };
}

function patchPeople(view: PhoneScreenView, ids: ReadonlySet<string>, patch: (person: PhoneScreenPerson) => PhoneScreenPerson): PhoneScreenView {
  return { ...view, people: view.people.map((person) => (ids.has(person.applicationId) ? patch(person) : person)) };
}

/** The server's copy has landed — only now does the optimistic state let go (§5a rule 3). */
async function settle(queryClient: QueryClient, jobId: string): Promise<void> {
  await Promise.all([queryClient.invalidateQueries({ queryKey: ['applications'] }), queryClient.invalidateQueries({ queryKey: ['shortlists', jobId] })]);
}

function usePending(): readonly [ReadonlySet<string>, (ids: readonly string[], isOn: boolean) => void] {
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const mark = useCallback((ids: readonly string[], isOn: boolean) => {
    setPending((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (isOn) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);
  return [pending, mark];
}

/** What a result does to one person, drawn before the server answers. */
export function afterResult(person: PhoneScreenPerson, round: number, result: PhoneScreenResult): PhoneScreenPerson {
  if (result === 'pass') return { ...person, passedRound: round };
  return { ...person, isInStep: false, outRound: round };
}

/**
 * F24.15 Pass and F24.16 Out, one tap each. Pass's Undo takes the pass back; Out's Undo is the pipeline's
 * own undo of the rejection (its regret email is still waiting, so it is cancelled with it).
 */
export function usePhoneScreenResult(jobId: string): {
  readonly record: (person: PhoneScreenPerson, round: number, result: PhoneScreenResult) => Promise<void>;
  readonly pending: ReadonlySet<string>;
} {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const [pending, mark] = usePending();
  const record = useCallback(
    async (person: PhoneScreenPerson, round: number, result: PhoneScreenResult): Promise<void> => {
      const id = person.applicationId;
      const name = person.candidate.fullName;
      const restore = draw(queryClient, jobId, (view) => patchPeople(view, new Set([id]), (row) => afterResult(row, round, result)));
      mark([id], true);
      try {
        const response = await apiPost<PhoneScreenResultResponse>(`/jobs/${jobId}/phone-screen/results`, { applicationId: id, round, result });
        await settle(queryClient, jobId);
        const roundName = phoneScreenRoundName(round);
        if (result === 'pass') {
          showUndo(`${name} passed ${roundName}`, () => {
            const back = draw(queryClient, jobId, (view) => patchPeople(view, new Set([id]), (row) => ({ ...row, passedRound: round - 1 })));
            void apiPost(`/jobs/${jobId}/phone-screen/results/undo-pass`, { applicationId: id, round })
              .then(() => settle(queryClient, jobId))
              .catch((error: unknown) => {
                back();
                show({ tone: 'danger', message: "Couldn't undo that", detail: errorMessage(error) });
              });
          });
          return;
        }
        const message = `${name} screened out in ${roundName}`;
        if (response.actionId === null) {
          show({ tone: 'success', message });
          return;
        }
        const actionId = response.actionId;
        showUndo(message, () => {
          const back = draw(queryClient, jobId, (view) => patchPeople(view, new Set([id]), (row) => ({ ...row, isInStep: true, outRound: null })));
          void undoAction(actionId)
            .then(() => settle(queryClient, jobId))
            .catch((error: unknown) => {
              back();
              show({ tone: 'danger', message: "Couldn't undo that", detail: errorMessage(error) });
            });
        });
      } catch (error) {
        restore();
        show({ tone: 'danger', message: `${name}: nothing changed`, detail: errorMessage(error) });
      } finally {
        mark([id], false);
      }
    },
    [queryClient, jobId, mark, show, showUndo],
  );
  return { record, pending };
}

/** F24.17: the ticked people, at any round, on to Contact. Undo brings them back while nobody has contacted them. */
export function useSendToContact(jobId: string): { readonly send: (people: readonly PhoneScreenPerson[]) => Promise<void> } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const send = useCallback(
    async (people: readonly PhoneScreenPerson[]): Promise<void> => {
      const moving = people.filter((person) => person.isInStep);
      if (moving.length === 0) {
        show({ tone: 'info', message: 'Nobody ticked is still in Screening' });
        return;
      }
      const ids = moving.map((person) => person.applicationId);
      const restore = draw(
        queryClient,
        jobId,
        (view) => ({ ...view, people: view.people.filter((person) => !ids.includes(person.applicationId)), waiting: Math.max(view.waiting - ids.length, 0) }),
        (bar) => shiftStageBar(bar, 'phone_screen', 'contact', ids.length),
      );
      try {
        const result = await apiPost<PhoneScreenMoveResult>(`/jobs/${jobId}/phone-screen/send-to-contact`, { applicationIds: ids });
        await settle(queryClient, jobId);
        const moved = result.moved;
        if (moved.length === 0) {
          show({ tone: 'warning', message: 'Nobody moved', detail: result.skipped[0]?.message });
          return;
        }
        const single = moving.find((person) => person.applicationId === moved[0]);
        const message = moved.length === 1 && single ? `${single.candidate.fullName} sent to Contact` : `${moved.length} sent to Contact`;
        showUndo(message, () => {
          const returning = moving.filter((person) => moved.includes(person.applicationId));
          const back = draw(
            queryClient,
            jobId,
            (view) => ({ ...view, people: [...view.people, ...returning], waiting: view.waiting + returning.length }),
            (bar) => shiftStageBar(bar, 'contact', 'phone_screen', returning.length),
          );
          void apiPost<PhoneScreenMoveResult>(`/jobs/${jobId}/phone-screen/back-to-screening`, { applicationIds: moved })
            .then(async (result) => {
              await settle(queryClient, jobId);
              if (result.skipped.length > 0) show({ tone: 'warning', message: `${result.skipped.length} stayed at Contact`, detail: result.skipped[0]?.message });
            })
            .catch((error: unknown) => {
              back();
              show({ tone: 'danger', message: "Couldn't undo that", detail: errorMessage(error) });
            });
        });
      } catch (error) {
        restore();
        show({ tone: 'danger', message: 'Nobody was sent to Contact', detail: errorMessage(error) });
      }
    },
    [queryClient, jobId, show, showUndo],
  );
  return { send };
}

/** F24.13 from the Shortlist. On shows at once; a refusal to switch off ("2 people are still in Screening") puts it back and says why. */
export function useSwitchPhoneScreen(jobId: string): { readonly setOn: (isOn: boolean) => Promise<void>; readonly isSaving: boolean } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [isSaving, setIsSaving] = useState(false);
  const setOn = useCallback(
    async (isOn: boolean): Promise<void> => {
      const restore = draw(queryClient, jobId, (view) => ({ ...view, isOn }));
      setIsSaving(true);
      try {
        const view = await apiPut<PhoneScreenView>(`/jobs/${jobId}/phone-screen/switch`, { isOn });
        queryClient.setQueryData(phoneScreenKey(jobId), view);
        await settle(queryClient, jobId);
        show(
          isOn
            ? { tone: 'success', message: 'Screening is on for this job', detail: view.waiting > 0 ? `${view.waiting} ${view.waiting === 1 ? 'person is' : 'people are'} waiting in it.` : 'People the manager says yes to go there before Contact.' }
            : { tone: 'success', message: 'Screening is off for this job', detail: 'People go straight to Contact again.' },
        );
      } catch (error) {
        restore();
        show({ tone: 'danger', message: isOn ? "Screening wasn't switched on" : "Screening wasn't switched off", detail: errorMessage(error) });
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient, jobId, show],
  );
  return { setOn, isSaving };
}

/** A round drawn before the server has numbered it; the server's view replaces it. */
const DRAWN_ROUND_ID = 'drawn-round';

/** F24.14: add the next screening, change what one checks, or take the last empty one away — each drawn at once. */
export function usePhoneScreenRounds(jobId: string): {
  readonly add: (topic: string) => Promise<boolean>;
  readonly rename: (roundId: string, version: number, topic: string) => Promise<boolean>;
  readonly remove: (roundId: string, version: number) => Promise<boolean>;
} {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const save = useCallback(
    async (change: (view: PhoneScreenView) => PhoneScreenView, run: () => Promise<PhoneScreenView>, failure: string): Promise<boolean> => {
      const restore = draw(queryClient, jobId, change);
      try {
        queryClient.setQueryData(phoneScreenKey(jobId), await run());
        await settle(queryClient, jobId);
        return true;
      } catch (error) {
        restore();
        show({ tone: 'danger', message: failure, detail: errorMessage(error) });
        return false;
      }
    },
    [queryClient, jobId, show],
  );
  const add = useCallback(
    (topic: string) =>
      save(
        (view) => ({ ...view, rounds: [...view.rounds, drawnRound(view, topic)] }),
        () => apiPost<PhoneScreenView>(`/jobs/${jobId}/phone-screen/rounds`, { topic }),
        'The screening was not added',
      ),
    [save, jobId],
  );
  const rename = useCallback(
    (roundId: string, version: number, topic: string) =>
      save(
        (view) => ({ ...view, rounds: view.rounds.map((round) => (round.id === roundId ? { ...round, topic } : round)) }),
        () => apiPut<PhoneScreenView>(`/phone-screen-rounds/${roundId}`, { topic, version }),
        'The screening was not changed',
      ),
    [save],
  );
  const remove = useCallback(
    (roundId: string, version: number) =>
      save(
        (view) => ({ ...view, rounds: view.rounds.filter((round) => round.id !== roundId) }),
        () => apiDelete<PhoneScreenView>(`/phone-screen-rounds/${roundId}?version=${version}`),
        'The screening stays',
      ),
    [save],
  );
  return { add, rename, remove };
}

/** The next round as the server will make it: everyone who passed every round so far waits in it. */
export function drawnRound(view: PhoneScreenView, topic: string): PhoneScreenView['rounds'][number] {
  const number = view.rounds.length + 1;
  const waiting = view.people.filter((person) => person.isInStep && person.passedRound === number - 1).length;
  return { id: DRAWN_ROUND_ID, number, topic, createdByName: null, createdAt: new Date().toISOString(), waiting, passed: 0, out: 0, version: 1 };
}
